/*
 * Bundle Builder — vanilla JS controller
 * Mirrors the headless /bundles page behavior for Horizons theme.
 *
 * Reads config + product data from a JSON script tag inside the section root,
 * handles size filtering, selection, mobile sticky footer, and AJAX cart add
 * with line item properties + optional selling plan.
 */
(function () {
  'use strict';

  function formatPrice(cents) {
    return '$' + (Number(cents) / 100).toFixed(2);
  }

  function init(root) {
    if (root.dataset.bundleBuilderInit === '1') return;
    root.dataset.bundleBuilderInit = '1';

    var configEl = root.querySelector('[data-bundle-config]');
    if (!configEl) return;

    var config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (e) {
      console.error('Bundle builder: invalid config JSON', e);
      return;
    }

    var bundleSize = Number(config.bundleSize) || 3;
    var bundlePriceCents = Number(config.bundlePriceCents) || 3900;
    var bundleVariantId = String(config.bundleVariantId || '');
    var sellingPlanId = config.sellingPlanId ? String(config.sellingPlanId) : '';
    var cartAddUrl = config.cartAddUrl || '/cart/add.js';

    // Subscription vs one-time: the subscribe toggle switches both the variant
    // added to cart and the price shown. Falls back to the single bundle
    // variant/price when the product has no "Plan" option.
    var oneTimeVariantId = String(config.oneTimeVariantId || config.bundleVariantId || '');
    var subscriptionVariantId = String(config.subscriptionVariantId || config.bundleVariantId || '');
    var oneTimePriceCents =
      config.oneTimePriceCents != null ? Number(config.oneTimePriceCents) : bundlePriceCents;
    var subscriptionPriceCents =
      config.subscriptionPriceCents != null ? Number(config.subscriptionPriceCents) : bundlePriceCents;
    var products = Array.isArray(config.products) ? config.products : [];

    // Build a lookup of variant -> { product, variant }
    var variantMap = Object.create(null);
    products.forEach(function (p) {
      p.variants.forEach(function (v) {
        variantMap[v.id] = { product: p, variant: v };
      });
    });

    // State
    var selected = []; // array of variant IDs (numeric strings)
    var subscribe = !!config.defaultSubscribe;
    var sizeFilter = 'all';
    var isSubmitting = false;

    // DOM refs
    var grid = root.querySelector('[data-bb-grid]');
    var counter = root.querySelector('[data-bb-counter]');
    var summaryCount = root.querySelector('[data-bb-summary-count]');
    var filters = root.querySelectorAll('[data-bb-filter]');
    var emptyGridMsg = root.querySelector('[data-bb-empty]');
    var summary = root.querySelector('[data-bb-summary]');
    var summaryItems = summary.querySelector('[data-bb-summary-items]');
    var summaryProgress = summary.querySelector('[data-bb-progress]');
    var summarySub = summary.querySelector('[data-bb-sub]');
    var summarySubCheckbox = summary.querySelector('[data-bb-sub-checkbox]');
    var summaryPrice = summary.querySelector('[data-bb-price]');
    var summaryCta = summary.querySelector('[data-bb-cta]');
    var summaryError = summary.querySelector('[data-bb-error]');
    var mobile = root.querySelector('[data-bb-mobile]');
    var mobileRow = mobile && mobile.querySelector('[data-bb-mobile-row]');
    var mobileCta = mobile && mobile.querySelector('[data-bb-mobile-cta]');

    // ---------- Helpers ----------

    // Active variant + price follow the subscribe toggle.
    function activePriceCents() {
      return subscribe ? subscriptionPriceCents : oneTimePriceCents;
    }
    function activeVariantId() {
      return subscribe ? subscriptionVariantId : oneTimeVariantId;
    }

    function isVariantEligible(variant) {
      if (!variant.available) return false;
      if (variant.inventoryTracked && variant.inventory != null && variant.inventory <= 0) {
        return false;
      }
      if (sizeFilter === 'all') return true;
      var size = variant.size || '';
      return size === sizeFilter || size === 'OSFM';
    }

    function pickVariant(product) {
      // Prefer a currently-selected variant of this product (so we don't switch
      // a user's selection out from under them when they toggle the size filter).
      for (var i = 0; i < selected.length; i++) {
        var entry = variantMap[selected[i]];
        if (entry && entry.product.id === product.id) {
          if (isVariantEligible(entry.variant)) {
            return entry.variant;
          }
        }
      }
      for (var j = 0; j < product.variants.length; j++) {
        if (isVariantEligible(product.variants[j])) {
          return product.variants[j];
        }
      }
      return null;
    }

    function findCard(productId) {
      return grid.querySelector('[data-bb-product-id="' + productId + '"]');
    }

    // ---------- Renderers ----------

    function renderGrid() {
      var visibleCount = 0;
      var isFull = selected.length >= bundleSize;

      products.forEach(function (p) {
        var card = findCard(p.id);
        if (!card) return;

        var variant = pickVariant(p);
        if (!variant) {
          card.classList.add('is-hidden');
          card.setAttribute('aria-hidden', 'true');
          return;
        }

        visibleCount++;
        card.classList.remove('is-hidden');
        card.removeAttribute('aria-hidden');

        // Update current variant data
        card.dataset.bbVariantId = variant.id;

        var imgEl = card.querySelector('[data-bb-card-image]');
        if (imgEl && variant.image) {
          if (imgEl.dataset.bbImage !== variant.image) {
            imgEl.src = variant.image;
            imgEl.dataset.bbImage = variant.image;
          }
        }

        var sizeEl = card.querySelector('[data-bb-card-size]');
        if (sizeEl) {
          if (variant.size) {
            sizeEl.textContent = variant.size;
            sizeEl.style.display = '';
          } else {
            sizeEl.style.display = 'none';
          }
        }

        var priceEl = card.querySelector('[data-bb-card-price]');
        if (priceEl) {
          priceEl.textContent = formatPrice(variant.priceCents);
        }

        var isSelected = selected.indexOf(variant.id) !== -1;
        card.classList.toggle('is-selected', isSelected);
        card.classList.toggle('is-disabled', isFull && !isSelected);
        if (isFull && !isSelected) {
          card.setAttribute('disabled', '');
        } else {
          card.removeAttribute('disabled');
        }
      });

      if (emptyGridMsg) {
        emptyGridMsg.style.display = visibleCount === 0 ? '' : 'none';
      }
    }

    function renderSummary() {
      var count = selected.length;
      var emptySlots = bundleSize - count;
      var isComplete = count === bundleSize;

      // Counter chip
      if (counter) {
        counter.textContent = count + ' / ' + bundleSize + ' selected';
      }
      if (summaryCount) {
        summaryCount.textContent = String(count);
      }

      // Progress
      if (summaryProgress) {
        summaryProgress.style.width = (count / bundleSize) * 100 + '%';
      }

      // Items list
      if (summaryItems) {
        var html = '';
        selected.forEach(function (vid) {
          var entry = variantMap[vid];
          if (!entry) return;
          var p = entry.product;
          var v = entry.variant;
          var img = v.image || p.image || '';
          var variantLabel = v.title && v.title !== 'Default Title' ? v.title : '';
          html +=
            '<div class="bundle-summary__item" data-bb-item="' + vid + '">' +
            '<div class="bundle-summary__item-thumb">' +
            (img
              ? '<img src="' + img + '" alt="' + escapeAttr(p.title) + '">'
              : '') +
            '</div>' +
            '<div class="bundle-summary__item-body">' +
            '<p class="bundle-summary__item-title">' + escapeHtml(p.title) + '</p>' +
            (variantLabel
              ? '<p class="bundle-summary__item-variant">' + escapeHtml(variantLabel) + '</p>'
              : '') +
            '</div>' +
            '<button type="button" class="bundle-summary__item-remove" aria-label="Remove ' +
            escapeAttr(p.title) +
            '" data-bb-remove="' + vid + '">' +
            '<svg fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>' +
            '</button>' +
            '</div>';
        });

        for (var i = 0; i < emptySlots; i++) {
          html +=
            '<div class="bundle-summary__empty">' +
            '<div class="bundle-summary__empty-thumb">+</div>' +
            '<span class="bundle-summary__empty-label">Choose a sock</span>' +
            '</div>';
        }
        summaryItems.innerHTML = html;
      }

      // Subscription box state
      if (summarySub) {
        summarySub.classList.toggle('is-active', subscribe);
      }
      if (summarySubCheckbox) {
        summarySubCheckbox.checked = subscribe;
      }

      // Bundle price follows the toggle (subscription price vs one-time).
      if (summaryPrice) {
        summaryPrice.textContent = formatPrice(activePriceCents());
      }

      // CTA
      if (summaryCta) {
        if (isSubmitting) {
          summaryCta.textContent = 'Adding to cart...';
          summaryCta.classList.remove('is-ready');
          summaryCta.disabled = true;
        } else if (isComplete) {
          var ctaPrice = formatPrice(activePriceCents());
          summaryCta.textContent =
            'Add to Cart — ' + ctaPrice + (subscribe ? ' + subscription' : '');
          summaryCta.classList.add('is-ready');
          summaryCta.disabled = false;
        } else {
          summaryCta.textContent =
            'Select ' + emptySlots + ' more sock' + (emptySlots === 1 ? '' : 's');
          summaryCta.classList.remove('is-ready');
          summaryCta.disabled = false;
        }
      }

      // Mobile footer
      if (mobile) {
        mobile.classList.toggle('is-visible', count > 0);

        if (count > 0) {
          root.classList.add('is-floating-summary');
        } else {
          root.classList.remove('is-floating-summary');
        }
      }
      if (mobileRow) {
        var mhtml = '';
        selected.forEach(function (vid) {
          var entry = variantMap[vid];
          if (!entry) return;
          var img = entry.variant.image || entry.product.image || '';
          mhtml +=
            '<div class="bundle-mobile__thumb">' +
            '<div class="bundle-mobile__thumb-img">' +
            (img ? '<img src="' + img + '" alt="' + escapeAttr(entry.product.title) + '">' : '') +
            '</div>' +
            '<button type="button" class="bundle-mobile__thumb-remove" data-bb-remove="' + vid + '" aria-label="Remove ' + escapeAttr(entry.product.title) + '">' +
            '<svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>' +
            '</button>' +
            '</div>';
        });
        for (var k = 0; k < emptySlots; k++) {
          mhtml += '<div class="bundle-mobile__empty">+</div>';
        }
        mhtml +=
          '<div class="bundle-mobile__totals">' +
          '<p class="bundle-mobile__count" data-bb-mobile-count>' + count + '/' + bundleSize + ' socks</p>' +
          '<p class="bundle-mobile__price" data-bb-mobile-price>' + formatPrice(activePriceCents()) + '</p>' +
          '</div>';
        mobileRow.innerHTML = mhtml;
      }
      if (mobileCta) {
        if (isComplete) {
          mobileCta.textContent = 'Review Bundle & Add to Cart';
          mobileCta.classList.add('is-ready');
        } else {
          mobileCta.textContent =
            'Pick ' + emptySlots + ' more sock' + (emptySlots === 1 ? '' : 's');
          mobileCta.classList.remove('is-ready');
        }
      }
    }

    function renderFilters() {
      filters.forEach(function (btn) {
        btn.classList.toggle('is-active', btn.dataset.bbFilter === sizeFilter);
      });
    }

    function render() {
      renderFilters();
      renderGrid();
      renderSummary();
    }

    // ---------- Event handlers ----------

    function toggleVariant(variantId) {
      var idx = selected.indexOf(variantId);
      if (idx !== -1) {
        selected.splice(idx, 1);
        render();
        return;
      }
      if (selected.length >= bundleSize) return;
      selected.push(variantId);

      var wasJustCompleted = selected.length === bundleSize;
      render();

      // Auto-scroll on mobile when bundle completes
      if (wasJustCompleted && window.innerWidth < 1024 && summary) {
        summary.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    function removeVariant(variantId) {
      var idx = selected.indexOf(variantId);
      if (idx === -1) return;
      selected.splice(idx, 1);
      render();
    }

    function clearError() {
      if (!summaryError) return;
      summaryError.classList.remove('is-visible');
      summaryError.textContent = '';
    }

    function showError(msg) {
      if (!summaryError) return;
      summaryError.textContent = msg;
      summaryError.classList.add('is-visible');
    }

    function buildSockTitles() {
      return selected.map(function (vid) {
        var entry = variantMap[vid];
        if (!entry) return '';
        var v = entry.variant;
        var variantTitle = v.title && v.title !== 'Default Title' ? ' (' + v.title + ')' : '';
        return entry.product.title + variantTitle;
      });
    }

    function getCartSectionIds() {
      var ids = [];
      var nodes = document.querySelectorAll('cart-items-component[data-section-id]');
      nodes.forEach(function (n) {
        var sid = n.getAttribute('data-section-id');
        if (sid) ids.push(sid);
      });
      return ids;
    }

    function checkout() {
      if (isSubmitting) return;
      if (selected.length !== bundleSize) return;
      if (!activeVariantId()) {
        showError('This bundle is not available right now. Please try again later or contact us.');
        return;
      }
      clearError();
      isSubmitting = true;
      renderSummary();

      var cleanIds = selected.map(function (id) {
        return id;
      });
      var sockTitles = buildSockTitles();

      var item = {
        id: Number(activeVariantId()),
        quantity: 1,
        properties: {
          'Selected Socks': sockTitles.join(' | '),
          _sock_variants: cleanIds.join('|'),
        },
      };
      if (subscribe && sellingPlanId) {
        item.selling_plan = Number(sellingPlanId);
      }

      var body = { items: [item] };
      var sectionIds = getCartSectionIds();
      if (sectionIds.length) {
        body.sections = sectionIds.join(',');
      }

      fetch(cartAddUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      })
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok) {
              var msg = data && (data.description || data.message)
                ? data.description || data.message
                : 'Could not add bundle to cart.';
              throw new Error(msg);
            }
            return data;
          });
        })
        .then(function (data) {
          // Reset selections so the user can build another bundle if they want.
          selected = [];
          isSubmitting = false;
          render();

          // Dispatch theme cart-update event so cart drawer auto-opens.
          var event = new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: data,
              sourceId: 'bundle-builder',
              data: {
                source: 'bundle-builder',
                itemCount: 1,
                variantId: activeVariantId(),
                productId: data && data.product_id ? String(data.product_id) : undefined,
                sections: data && data.sections,
              },
            },
          });
          document.dispatchEvent(event);
        })
        .catch(function (err) {
          isSubmitting = false;
          showError(err && err.message ? err.message : 'Something went wrong. Please try again.');
          renderSummary();
        });
    }

    // ---------- Wire up ----------

    // Card click → toggle selection
    grid.addEventListener('click', function (e) {
      var card = e.target.closest('[data-bb-card]');
      if (!card || !grid.contains(card)) return;
      if (card.classList.contains('is-disabled') || card.hasAttribute('disabled')) return;
      var vid = card.dataset.bbVariantId;
      if (vid) toggleVariant(vid);
    });

    // Remove (X) buttons (delegated for both summary and mobile)
    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-bb-remove]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      var vid = btn.dataset.bbRemove;
      if (vid) removeVariant(vid);
    });

    // Size filter
    filters.forEach(function (btn) {
      btn.addEventListener('click', function () {
        sizeFilter = btn.dataset.bbFilter || 'all';
        render();
      });
    });

    // Subscribe toggle
    if (summarySubCheckbox) {
      summarySubCheckbox.addEventListener('change', function () {
        subscribe = !!summarySubCheckbox.checked;
        renderSummary();
      });
    }

    // CTA
    if (summaryCta) {
      summaryCta.addEventListener('click', function () {
        if (selected.length === bundleSize) {
          checkout();
        }
      });
    }

    // Mobile CTA scrolls up to the summary (mirrors headless behavior)
    if (mobileCta) {
      mobileCta.addEventListener('click', function () {
        if (selected.length === bundleSize) {
          checkout();
        } else if (summary) {
          summary.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }

    // Initial render
    render();
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
  function escapeAttr(s) {
    return escapeHtml(s).replace(/"/g, '&quot;');
  }

  function start() {
    document.querySelectorAll('[data-bundle-builder]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Re-init on Shopify theme editor section load
  document.addEventListener('shopify:section:load', function (e) {
    var target = e.target;
    if (!target) return;
    var section =
      target.matches && target.matches('[data-bundle-builder]')
        ? target
        : target.querySelector && target.querySelector('[data-bundle-builder]');
    if (section) init(section);
  });
})();
